import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuthStore } from '@/stores/authStore'
import AppLayout from '@/components/layout/AppLayout'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import Screening from '@/pages/Screening'
import PortfolioAnalysis from '@/pages/PortfolioAnalysis'
import PrivateMarkets from '@/pages/PrivateMarkets'
import PortfolioBook from '@/pages/PortfolioBook'
import QISConstruction from '@/pages/QISConstruction'
import StrategyLab from '@/pages/StrategyLab'
import BenchmarkMapping from '@/pages/BenchmarkMapping'
import ManagerSelection from '@/pages/ManagerSelection'
import AiCio from '@/pages/AiCio'
import PortfolioSimulation from '@/pages/PortfolioSimulation'
import DemoPortfolios from '@/pages/DemoPortfolios'
import RiskDashboard from '@/pages/RiskDashboard'
import OperaMonitoring from '@/pages/OperaMonitoring'
import AUMMonitoring from '@/pages/AUMMonitoring'
import HighFreqReturns from '@/pages/HighFreqReturns'
import FactorModeling from '@/pages/FactorModeling'
import PortfolioFactors from '@/pages/PortfolioFactors'
import WhatsWorking from '@/pages/WhatsWorking'
import HFAnalytics from '@/pages/HFAnalytics'
import PersonnelMonitoring from '@/pages/PersonnelMonitoring'
import DataValidation from '@/pages/DataValidation'
import DataExplorer from '@/pages/DataExplorer'
import InsertReturns from '@/pages/InsertReturns'
import DocumentCompleteness from '@/pages/DocumentCompleteness'
import UserManagement from '@/pages/UserManagement'
import UserProfile from '@/pages/UserProfile'
import PeerGroupAnalysis from '@/pages/PeerGroupAnalysis'
import NetworkAnalysis from '@/pages/NetworkAnalysis'
import PortfolioReturns from '@/pages/PortfolioReturns'
import RedemptionCalculator from '@/pages/RedemptionCalculator'
import DataProviders from '@/pages/DataProviders'
import FundDataUpload from '@/pages/FundDataUpload'
import Documentation from '@/pages/Documentation'
import FundManagement from '@/pages/FundManagement'
import Instruments from '@/pages/Instruments'
import FundDetail from '@/pages/FundDetail'
import FundIdentity from '@/pages/FundIdentity'
import FundAnalysis from '@/pages/FundAnalysis'
import RiskOverview from '@/pages/RiskOverview'
import RiskVaR from '@/pages/RiskVaR'
import RiskStress from '@/pages/RiskStress'
import RiskFactor from '@/pages/RiskFactor'
import RiskRuns from '@/pages/RiskRuns'
import RiskEnsemble from '@/pages/RiskEnsemble'
import RiskReportView from '@/pages/RiskReportView'
import RiskBacktest from '@/pages/RiskBacktest'
import RiskAttribution from '@/pages/RiskAttribution'
import RiskPeers from '@/pages/RiskPeers'
import AdminJobs from '@/pages/AdminJobs'
import NotFound from '@/pages/NotFound'

// Heavy: pulls in the pdfme designer — kept out of the main bundle
const ReportCenter = lazy(() => import('@/pages/ReportCenter'))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="spinner" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()

  if (user?.role !== 'Admin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="documentation" element={<Documentation />} />
        <Route path="screening" element={<Screening />} />
        <Route path="screening/:fundId" element={<FundDetail />} />
        <Route path="fund" element={<FundIdentity />} />
        <Route path="fund/:fundId" element={<FundIdentity />} />
        <Route path="whats-working" element={<WhatsWorking />} />
        <Route path="performance-analytics" element={<HFAnalytics />} />
        <Route path="portfolio" element={<PortfolioAnalysis />} />
        <Route path="private" element={<PrivateMarkets />} />
        <Route path="book" element={<PortfolioBook />} />
        <Route path="qis" element={<QISConstruction />} />
        <Route path="strategy-lab" element={<StrategyLab />} />
        <Route path="benchmarks" element={<BenchmarkMapping />} />
        <Route path="manager-selection" element={<ManagerSelection />} />
        <Route path="ai-cio" element={<AiCio />} />
        <Route path="reports" element={<Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading report designer…</div>}><ReportCenter /></Suspense>} />
        <Route path="portfolio/simulation" element={<PortfolioSimulation />} />
        <Route path="portfolio/demo" element={<DemoPortfolios />} />
        <Route path="portfolio/returns" element={<PortfolioReturns />} />
        <Route path="risk" element={<RiskDashboard />} />
        <Route path="risk-stats" element={<RiskOverview />} />
        <Route path="risk-stats/var" element={<RiskVaR />} />
        <Route path="risk-stats/stress" element={<RiskStress />} />
        <Route path="risk-stats/factor-model" element={<RiskFactor />} />
        <Route path="risk-stats/attribution" element={<RiskAttribution />} />
        <Route path="risk-stats/runs" element={<RiskRuns />} />
        <Route path="risk-stats/ensemble" element={<RiskEnsemble />} />
        <Route path="risk-stats/backtest" element={<RiskBacktest />} />
        <Route path="risk-stats/peers" element={<RiskPeers />} />
        <Route path="risk-stats/report" element={<RiskReportView />} />
        <Route
          path="admin/jobs"
          element={
            <AdminRoute>
              <AdminJobs />
            </AdminRoute>
          }
        />
        <Route path="opera" element={<OperaMonitoring />} />
        <Route path="aum" element={<AUMMonitoring />} />
        <Route path="high-freq" element={<HighFreqReturns />} />
        <Route path="fund-analysis" element={<FundAnalysis />} />
        <Route path="factors" element={<FactorModeling />} />
        <Route path="factors/attribution" element={<PortfolioFactors />} />
        <Route path="peer-group" element={<PeerGroupAnalysis />} />
        <Route path="network" element={<NetworkAnalysis />} />
        <Route path="redemption" element={<RedemptionCalculator />} />
        <Route path="personnel" element={<PersonnelMonitoring />} />
        <Route path="data/explorer" element={<DataExplorer />} />
        <Route path="data/validation" element={<DataValidation />} />
        <Route path="data/returns" element={<InsertReturns />} />
        <Route path="data/docs" element={<DocumentCompleteness />} />
        <Route path="data/providers" element={<DataProviders />} />
        <Route path="data/funds" element={<FundManagement />} />
        <Route path="data/upload" element={<FundDataUpload />} />
        <Route path="data/instruments" element={<Instruments />} />
        <Route path="profile" element={<UserProfile />} />
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App
